import { useQuery } from '@tanstack/react-query';
import type { MailInventoryDto, MailWorkspaceDomainDto } from '@cpm/shared';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { Card, CardHeader, ErrorNote, Spinner } from './ui.js';
import { SubscriptionWebmailPanel } from './webmail-panel.js';
export function mailBytes(bytes: number | null): string {
  if (bytes === null) return 'Unavailable';
  for (const [scale, label] of [
    [1024 ** 3, 'GB'],
    [1024 ** 2, 'MB'],
    [1024, 'KB'],
  ] as const) {
    if (bytes >= scale) return `${(bytes / scale).toFixed(1)} ${label}`;
  }
  return `${bytes} B`;
}
export function SubscriptionMailSummary({
  subscriptionId,
  portal = false,
}: {
  subscriptionId: string;
  portal?: boolean;
}) {
  const data = useQuery({
    queryKey: ['mail', 'domains', subscriptionId],
    queryFn: () =>
      api.get<MailInventoryDto<MailWorkspaceDomainDto>>(
        `/api/mail/domains?subscriptionId=${subscriptionId}&pageSize=100`,
      ),
  });
  return (
    <Card>
      <CardHeader
        title="Mail"
        actions={
          <Link
            className="text-sm font-medium text-brand-600"
            to={`${portal ? '/portal/mail' : '/mail'}?subscriptionId=${subscriptionId}`}
          >
            Manage mail
          </Link>
        }
      />
      <div className="space-y-3 px-5 pb-4">
        {data.isPending ? (
          <Spinner />
        ) : data.error ? (
          <ErrorNote error={data.error} />
        ) : (
          <p className="text-sm">
            {data.data.total} domains ·{' '}
            {data.data.items.reduce((sum, d) => sum + d.mailboxes.length, 0)} mailboxes ·{' '}
            {mailBytes(data.data.items.reduce((sum, d) => sum + d.knownUsedBytes, 0))} known storage
            {data.data.items.some((d) => d.unknownUsageCount > 0) ||
            data.data.total > data.data.items.length
              ? ' (partial total)'
              : ''}
          </p>
        )}
        {!portal && <SubscriptionWebmailPanel subscriptionId={subscriptionId} />}
      </div>
    </Card>
  );
}
